import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    FormControlLabel
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    Security as SecurityIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const Roles = () => {
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]); // Array of all permissions
    const [groupedPermissions, setGroupedPermissions] = useState({});
    const [loading, setLoading] = useState(false);

    // Dialog States
    const [openDialog, setOpenDialog] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentRole, setCurrentRole] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        permissions: [] // Array of permission IDs
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [rolesRes, permsRes] = await Promise.all([
                api.get('/rbac/roles'),
                api.get('/rbac/permissions')
            ]);
            setRoles(rolesRes.data.data);
            setPermissions(permsRes.data.data);
            setGroupedPermissions(permsRes.data.grouped);
        } catch (error) {
            toast.error("Failed to fetch RBAC data");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (role = null) => {
        if (role) {
            setIsEditing(true);
            setCurrentRole(role);
            setFormData({
                name: role.name,
                description: role.description || '',
                permissions: role.permissions.map(p => p.id)
            });
        } else {
            setIsEditing(false);
            setCurrentRole(null);
            setFormData({ name: '', description: '', permissions: [] });
        }
        setOpenDialog(true);
    };

    const handleSubmit = async () => {
        if (!formData.name) return toast.warning("Name is required");

        try {
            if (isEditing) {
                await api.put(`/rbac/roles/${currentRole.id}`, formData);
                toast.success('Role updated successfully');
            } else {
                await api.post('/rbac/roles', formData);
                toast.success('Role created successfully');
            }
            setOpenDialog(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleDelete = async (roleId) => {
        if (!window.confirm('Are you sure you want to delete this role?')) return;
        try {
            await api.delete(`/rbac/roles/${roleId}`);
            toast.success('Role deleted');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Delete failed');
        }
    };

    const togglePermission = (permId) => {
        setFormData(prev => {
            const newPerms = prev.permissions.includes(permId)
                ? prev.permissions.filter(id => id !== permId)
                : [...prev.permissions, permId];
            return { ...prev, permissions: newPerms };
        });
    };

    const toggleModule = (moduleName) => {
        const modulePerms = groupedPermissions[moduleName].map(p => p.id);
        const allSelected = modulePerms.every(id => formData.permissions.includes(id));

        setFormData(prev => {
            let newPerms = [...prev.permissions];
            if (allSelected) {
                // Unselect all
                newPerms = newPerms.filter(id => !modulePerms.includes(id));
            } else {
                // Select all (add missing)
                modulePerms.forEach(id => {
                    if (!newPerms.includes(id)) newPerms.push(id);
                });
            }
            return { ...prev, permissions: newPerms };
        });
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Role & Permission Management</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                    Create New Role
                </Button>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell><strong>Role Name</strong></TableCell>
                                    <TableCell><strong>Description</strong></TableCell>
                                    <TableCell><strong>Permissions Count</strong></TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell align="right"><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {roles.map(role => (
                                    <TableRow key={role.id} hover>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{role.name}</TableCell>
                                        <TableCell>{role.description}</TableCell>
                                        <TableCell>
                                            <Chip label={`${role.permissions?.length || 0} Permissions`} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            {role.isSystem ? <Chip label="System" size="small" color="primary" /> : <Chip label="Custom" size="small" />}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton color="primary" onClick={() => handleOpenDialog(role)}>
                                                <EditIcon />
                                            </IconButton>
                                            {!role.isSystem && (
                                                <IconButton color="error" onClick={() => handleDelete(role.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            {/* Role Dialog - Now with Permission Matrix! */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>{isEditing ? 'Edit Role' : 'Create Role'}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth label="Role Name"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                disabled={isEditing && currentRole?.isSystem}
                                helperText={isEditing && currentRole?.isSystem ? "System role names cannot be changed" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth label="Description"
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon color="primary" /> Permissions Assignment
                    </Typography>

                    {/* Permissions Matrix grouped by Module */}
                    {Object.keys(groupedPermissions).map(module => (
                        <Accordion key={module} defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f0f4f8' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                                    <FormControlLabel
                                        onClick={(e) => e.stopPropagation()}
                                        control={
                                            <Checkbox
                                                checked={groupedPermissions[module].every(p => formData.permissions.includes(p.id))}
                                                indeterminate={
                                                    groupedPermissions[module].some(p => formData.permissions.includes(p.id)) &&
                                                    !groupedPermissions[module].every(p => formData.permissions.includes(p.id))
                                                }
                                                onChange={() => toggleModule(module)}
                                            />
                                        }
                                        label={
                                            <Typography fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                                                {module} Module
                                            </Typography>
                                        }
                                    />
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={1}>
                                    {groupedPermissions[module].map(perm => (
                                        <Grid item xs={12} sm={6} md={4} key={perm.id}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.permissions.includes(perm.id)}
                                                        onChange={() => togglePermission(perm.id)}
                                                    />
                                                }
                                                label={<Typography variant="body2">{perm.name}</Typography>}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    ))}

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>Save Role</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Roles;
